/**
 * ---------------------------------------------------------------------------------------------------------------
 * BOTTOM NAVIGATION BAR
 * ---------------------------------------------------------------------------------------------------------------
 *
 * Custom element behind the "Bottom navigation bar" section.
 *
 *  - Scroll behaviour (always visible / hide on scroll down / hide after a threshold / reveal after a threshold)
 *  - Publishes `--bottom-navbar-height` (static), `--bottom-navbar-offset` (current visible height) and
 *    `--bottom-navbar-stack` (the bar plus anything docked on top of it, e.g. the product page sticky add to cart)
 *    on <html>, so any other floating element can sit clear of the whole stack
 *  - Highlights the item matching the current page, and the item whose drawer is open
 *  - Moves its menu sheets to <body> so drawers are never trapped inside the (transformed) fixed bar
 */

// Bars this theme docks on top of the navigation bar. Their height is added to `--bottom-navbar-stack` so that
// floating widgets clear them too — on a product page the add to cart bar sits between the widget and the nav bar.
const STACKED_BARS = '.ab-bottom-sticky-atc';

// Floating app widgets are lifted by CSS alone, from the selectors the merchant types into the section. There is no
// automatic scan: walking <body> to find "widget shaped" fixed elements meant reading layout on every DOM change and
// stamping `bottom: !important` onto elements this theme does not own — including third party checkout containers,
// which is exactly how it broke them.

if (!window.customElements.get('bottom-navigation-bar')) {
  class BottomNavigationBar extends HTMLElement {
    connectedCallback() {
      // Children are needed upfront; bail to DOMContentLoaded on the (unlikely) parser-first upgrade.
      if (!this.querySelector('.bottom-nav__inner')) {
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        return;
      }

      this._abortController = new AbortController();
      this._items = Array.from(this.querySelectorAll('.bottom-nav__item'));
      this._behavior = this.dataset.scrollBehavior || 'always';
      this._threshold = parseInt(this.dataset.scrollThreshold || '60', 10);
      this._revealAtBottom = this.dataset.revealAtBottom === 'true';
      this._lastScrollY = window.scrollY;
      this._ticking = false;

      // Nothing to show (every item hidden by its audience rules, or no blocks yet).
      if (this._items.length === 0) {
        this.style.display = 'none';
        return;
      }

      this._relocateSheets();
      this._bindActions();
      this._bindScroll();
      this._observeSize();
      this._markCurrentPage();
      this._watchDrawers();

      // In the editor the merchant must always be able to see what they are editing.
      if (window.Shopify?.designMode) {
        this._setHidden(false);
        this._bindDesignMode();
      } else {
        this._onScroll(true);
      }
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._resizeObserver?.disconnect();
      this._drawerObservers?.forEach((observer) => observer.disconnect());
      this._sheets?.forEach((sheet) => sheet.remove());
      document.documentElement.style.removeProperty('--bottom-navbar-height');
      document.documentElement.style.removeProperty('--bottom-navbar-offset');
      document.documentElement.style.removeProperty('--bottom-navbar-stack');
    }

    /* ------------------------------------------------------------------------------------------------------------
     * Theme editor — open the sheet of the item being edited
     * --------------------------------------------------------------------------------------------------------- */

    _bindDesignMode() {
      const sheetOf = (event) => {
        const item = event.target.closest?.('.bottom-nav__item');
        const sheetId = item?.getAttribute('aria-controls');
        const sheet = sheetId ? document.getElementById(sheetId) : null;

        return sheet?.hasAttribute('data-bottom-nav-sheet') ? sheet : null;
      };

      this.addEventListener('shopify:block:select', (event) => sheetOf(event)?.show(), {
        signal: this._abortController.signal
      });

      this.addEventListener('shopify:block:deselect', (event) => sheetOf(event)?.hide(), {
        signal: this._abortController.signal
      });
    }

    /* ------------------------------------------------------------------------------------------------------------
     * Sheets
     * --------------------------------------------------------------------------------------------------------- */

    _relocateSheets() {
      this._sheets = Array.from(this.querySelectorAll('[data-bottom-nav-sheet]'));

      this._sheets.forEach((sheet) => {
        // Cache the owning section before the move so the drawer keeps working in the theme editor.
        sheet._shopifySection = sheet._shopifySection || sheet.closest('.shopify-section');
        document.body.appendChild(sheet);
      });
    }

    /* ------------------------------------------------------------------------------------------------------------
     * Actions that are not plain links or drawer triggers
     * --------------------------------------------------------------------------------------------------------- */

    _bindActions() {
      this.addEventListener(
        'click',
        (event) => {
          const item = event.target.closest('[data-action]');

          if (!item || !this.contains(item)) {
            return;
          }

          if (item.dataset.action === 'scroll_top') {
            event.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? 'smooth' : 'auto'
            });
          } else if (item.dataset.action === 'back') {
            event.preventDefault();
            window.history.length > 1 ? window.history.back() : (window.location.href = item.dataset.fallbackUrl || '/');
          }
        },
        { signal: this._abortController.signal }
      );
    }

    /* ------------------------------------------------------------------------------------------------------------
     * Scroll behaviour
     * --------------------------------------------------------------------------------------------------------- */

    _bindScroll() {
      if (this._behavior === 'always' || window.Shopify?.designMode) {
        return;
      }

      window.addEventListener('scroll', () => this._onScroll(), {
        passive: true,
        signal: this._abortController.signal
      });
    }

    _onScroll(immediate = false) {
      if (this._ticking && !immediate) {
        return;
      }

      this._ticking = true;

      window.requestAnimationFrame(() => {
        this._ticking = false;

        const scrollY = Math.max(window.scrollY, 0);
        const delta = scrollY - this._lastScrollY;
        let hidden = this.classList.contains('is-hidden');

        switch (this._behavior) {
          case 'hide_on_scroll_down':
            if (scrollY <= this._threshold) {
              hidden = false;
            } else if (Math.abs(delta) > 4) {
              hidden = delta > 0;
            }
            break;

          case 'hide_after_scroll':
            hidden = scrollY > this._threshold;
            break;

          case 'show_after_scroll':
            hidden = scrollY <= this._threshold;
            break;

          default:
            hidden = false;
        }

        // Always give the bar back once the customer reaches the end of the page.
        if (hidden && this._revealAtBottom && this._behavior !== 'show_after_scroll') {
          const scrolledToBottom =
            window.innerHeight + scrollY >= document.documentElement.scrollHeight - 60;

          if (scrolledToBottom) {
            hidden = false;
          }
        }

        this._lastScrollY = scrollY;
        this._setHidden(hidden);
      });
    }

    _setHidden(hidden) {
      if (this.classList.contains('is-hidden') === hidden) {
        return;
      }

      this.classList.toggle('is-hidden', hidden);
      this._publishOffset();
    }

    /* ------------------------------------------------------------------------------------------------------------
     * Height / page spacing
     * --------------------------------------------------------------------------------------------------------- */

    _observeSize() {
      this._resizeObserver = new ResizeObserver(() => this._publishHeight());
      this._resizeObserver.observe(this);

      this._resolveStackedBar();
      this._publishHeight();
    }

    _publishHeight() {
      const height = Math.round(this.getBoundingClientRect().height);

      const stackedBar = this._resolveStackedBar();

      // Measuring happens here only: at connect, and inside the ResizeObserver callback where layout for the frame
      // is already computed, so neither read forces a reflow. Everything downstream reuses these numbers.
      this._height = height;
      this._stackedHeight = stackedBar ? Math.round(stackedBar.getBoundingClientRect().height) : 0;

      // Refines the value the section already published from its settings: this one accounts for wrapped labels,
      // bigger custom icons, and for the bar being display:none outside its breakpoint.
      document.documentElement.style.setProperty('--bottom-navbar-height', `${height}px`);
      this._publishOffset();
    }

    /**
     * The stacked bar is re-rendered by the theme (variant changes, section reloads), which leaves the cached node
     * detached and measuring 0. Resolve it again whenever it is no longer in the document.
     */
    _resolveStackedBar() {
      if (this._stackedBar && this._stackedBar.isConnected) {
        return this._stackedBar;
      }

      this._stackedBar = document.querySelector(STACKED_BARS);

      if (this._stackedBar) {
        // Its height changes with its own state — the "in cart" layout adds a quantity row — so measure, never assume.
        this._resizeObserver?.observe(this._stackedBar);
      }

      return this._stackedBar;
    }

    /**
     * Writes only. Called on every hide/show, so it must not read layout: `is-hidden` has just been toggled, and a
     * measurement here would force a synchronous reflow in the middle of a scroll.
     */
    _publishOffset() {
      const offset = this.classList.contains('is-hidden') ? 0 : this._height || 0;

      document.documentElement.style.setProperty('--bottom-navbar-offset', `${offset}px`);
      document.documentElement.style.setProperty('--bottom-navbar-stack', `${offset + (this._stackedHeight || 0)}px`);
    }

    /* ------------------------------------------------------------------------------------------------------------
     * Active state
     * --------------------------------------------------------------------------------------------------------- */

    _markCurrentPage() {
      if (this.dataset.highlightCurrent !== 'true') {
        return;
      }

      const currentPath = this._normalizePath(window.location.pathname);

      this._items.forEach((item) => {
        const target = item.dataset.activePath;

        if (!target) {
          return;
        }

        const targetPath = this._normalizePath(target);
        let active = targetPath === currentPath;

        if (!active && item.dataset.activeMatch === 'prefix' && targetPath !== '/') {
          active = currentPath.startsWith(`${targetPath}/`);
        }

        item._pageActive = active;
        item.classList.toggle('is-active', active);

        if (active) {
          item.setAttribute('aria-current', 'page');
        }
      });
    }

    _normalizePath(path) {
      try {
        const url = new URL(path, window.location.origin);
        const pathname = url.pathname.replace(/\/+$/, '');

        return pathname === '' ? '/' : pathname;
      } catch (error) {
        return path;
      }
    }

    _watchDrawers() {
      this._drawerObservers = [];

      this._items.forEach((item) => {
        const drawerId = item.getAttribute('aria-controls');
        const drawer = drawerId ? document.getElementById(drawerId) : null;

        if (!drawer) {
          return;
        }

        const sync = () => item.classList.toggle('is-active', drawer.hasAttribute('open') || item._pageActive === true);
        const observer = new MutationObserver(sync);

        observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
        this._drawerObservers.push(observer);
      });
    }
  }

  window.customElements.define('bottom-navigation-bar', BottomNavigationBar);
}
